import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';

// Shaders for water ripple effect
const simulationVertexShader = `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const simulationFragmentShader = `
uniform sampler2D textureA;
uniform vec2 mouse;
uniform vec2 resolution;
uniform float time;
uniform int frame;
varying vec2 vUv;

const float delta = 1.4;  

void main() {
    vec2 uv = vUv;
    if (frame == 0) {
        gl_FragColor = vec4(0.0);
        return;
    }
    
    vec4 data = texture2D(textureA, uv);
    float pressure = data.x;
    float pVel = data.y;
    
    vec2 texelSize = 1.0 / resolution;
    float p_right = texture2D(textureA, uv + vec2(texelSize.x, 0.0)).x;
    float p_left = texture2D(textureA, uv + vec2(-texelSize.x, 0.0)).x;
    float p_up = texture2D(textureA, uv + vec2(0.0, texelSize.y)).x;
    float p_down = texture2D(textureA, uv + vec2(0.0, -texelSize.y)).x;
    
    if (uv.x <= texelSize.x) p_left = p_right;
    if (uv.x >= 1.0 - texelSize.x) p_right = p_left;
    if (uv.y <= texelSize.y) p_down = p_up;
    if (uv.y >= 1.0 - texelSize.y) p_up = p_down;
    
    pVel += delta * (-2.0 * pressure + p_right + p_left) / 4.0;
    pVel += delta * (-2.0 * pressure + p_up + p_down) / 4.0;
    
    pressure += delta * pVel;
    
    pVel -= 0.005 * delta * pressure;
    
    pVel *= 1.0 - 0.002 * delta;
    pressure *= 0.999;
    
    vec2 mouseUV = mouse / resolution;
    if(mouse.x > 0.0) {
        float dist = distance(uv, mouseUV);
        if(dist <= 0.02) {
            pressure += 2.0 * (1.0 - dist / 0.02);
        }
    }
    
    gl_FragColor = vec4(pressure, pVel, 
        (p_right - p_left) / 2.0, 
        (p_up - p_down) / 2.0);
}
`;

const renderVertexShader = `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const renderFragmentShader = `
uniform sampler2D textureA;
uniform sampler2D textureB;
varying vec2 vUv;

void main() {
    vec4 data = texture2D(textureA, vUv);
    
    vec2 distortion = 0.3 * data.zw;
    vec4 color = texture2D(textureB, vUv + distortion);
    
    vec3 normal = normalize(vec3(-data.z * 2.0, 0.5, -data.w * 2.0));
    vec3 lightDir = normalize(vec3(-3.0, 10.0, 3.0));
    float specular = pow(max(0.0, dot(normal, lightDir)), 60.0) * 1.5;
    
    gl_FragColor = color + vec4(specular);
}
`;

export default function Explore() {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);

  // YouTube video ID
  const videoId = '8MPX86uzVWU';

  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    const simScene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const mouse = new THREE.Vector2();
    let frame = 0;

    const width = window.innerWidth * window.devicePixelRatio;
    const height = window.innerHeight * window.devicePixelRatio;
    const options: THREE.RenderTargetOptions = {
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      stencilBuffer: false,
      depthBuffer: false,
    };
    let rtA = new THREE.WebGLRenderTarget(width, height, options);
    let rtB = new THREE.WebGLRenderTarget(width, height, options);

    const simMaterial = new THREE.ShaderMaterial({
      uniforms: {
        textureA: { value: null },
        mouse: { value: mouse },
        resolution: { value: new THREE.Vector2(width, height) },
        time: { value: 0 },
        frame: { value: 0 },
      },
      vertexShader: simulationVertexShader,
      fragmentShader: simulationFragmentShader,
    });

    const renderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        textureA: { value: null },
        textureB: { value: null },
      },
      vertexShader: renderVertexShader,
      fragmentShader: renderFragmentShader,
      transparent: true,
    });

    const plane = new THREE.PlaneGeometry(2, 2);
    const simQuad = new THREE.Mesh(plane, simMaterial);
    const renderQuad = new THREE.Mesh(plane, renderMaterial);

    simScene.add(simQuad);
    scene.add(renderQuad);

    // Create canvas for text texture with transparent background
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: true });

    const drawCanvas = (w: number, h: number) => {
      if (!ctx) return;
      
      // Transparent background to show video underneath
      ctx.clearRect(0, 0, w, h);

      // Semi-transparent dark overlay
      ctx.fillStyle = 'rgba(10, 22, 40, 0.3)';
      ctx.fillRect(0, 0, w, h);

      // Main title text - light cyan/white
      const baseFontSize = Math.min(w * 0.08, 200 * window.devicePixelRatio);
      ctx.fillStyle = '#7dd3fc';
      ctx.font = `bold ${baseFontSize}px "Galano Grotesque", Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.textRendering = 'geometricPrecision';
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillText('In Deep Water', w / 2, h / 2);
    };

    drawCanvas(width, height);

    const textTexture = new THREE.CanvasTexture(canvas);
    textTexture.minFilter = THREE.LinearFilter;
    textTexture.magFilter = THREE.LinearFilter;
    textTexture.format = THREE.RGBAFormat;

    const handleResize = () => {
      const newWidth = window.innerWidth * window.devicePixelRatio;
      const newHeight = window.innerHeight * window.devicePixelRatio;

      renderer.setSize(window.innerWidth, window.innerHeight);
      rtA.setSize(newWidth, newHeight);
      rtB.setSize(newWidth, newHeight);
      simMaterial.uniforms.resolution.value.set(newWidth, newHeight);

      canvas.width = newWidth;
      canvas.height = newHeight;
      drawCanvas(newWidth, newHeight);
      textTexture.needsUpdate = true;
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX * window.devicePixelRatio;
      mouse.y = (window.innerHeight - e.clientY) * window.devicePixelRatio;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        mouse.x = touch.clientX * window.devicePixelRatio;
        mouse.y = (window.innerHeight - touch.clientY) * window.devicePixelRatio;
      }
    };

    const handleMouseLeave = () => {
      mouse.set(0, 0);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);
    renderer.domElement.addEventListener('mouseleave', handleMouseLeave);

    let animationId: number;
    const animate = () => {
      simMaterial.uniforms.frame.value = frame++;
      simMaterial.uniforms.time.value = performance.now() / 1000;

      simMaterial.uniforms.textureA.value = rtA.texture;
      renderer.setRenderTarget(rtB);
      renderer.render(simScene, camera);

      renderMaterial.uniforms.textureA.value = rtB.texture;
      renderMaterial.uniforms.textureB.value = textTexture;
      renderer.setRenderTarget(null);
      renderer.render(scene, camera);

      const temp = rtA;
      rtA = rtB;
      rtB = temp;

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      renderer.domElement.removeEventListener('mouseleave', handleMouseLeave);
      
      rtA.dispose();
      rtB.dispose();
      simMaterial.dispose();
      renderMaterial.dispose();
      plane.dispose();
      textTexture.dispose();
      renderer.dispose();
      
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <>
      <style>{`
        .explore-page {
          width: 100%;
          height: 100vh;
          background: #0a1628;
          position: fixed;
          inset: 0;
          z-index: 9998;
          overflow: hidden;
        }

        .explore-video-container {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100vw;
          height: 100vh;
          pointer-events: none;
          overflow: hidden;
          z-index: 1;
        }

        .explore-video-container iframe {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100vw;
          height: 56.25vw;
          min-height: 100vh;
          min-width: 177.77vh;
          pointer-events: none;
        }

        .explore-canvas-container {
          position: absolute;
          inset: 0;
          z-index: 2;
        }

        .explore-canvas-container canvas {
          position: absolute;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
        }

        .explore-nav {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          padding: 2em;
          display: flex;
          justify-content: space-between;
          align-items: center;
          z-index: 9999;
        }

        .explore-logo {
          font-size: 15px;
          color: #7dd3fc;
          font-weight: 500;
        }

        .explore-nav-items {
          display: flex;
          align-items: center;
          gap: 2em;
        }

        .explore-nav-items a {
          font-size: 15px;
          color: #7dd3fc;
          cursor: pointer;
          transition: opacity 0.2s;
          text-decoration: none;
          display: inline-block;
        }

        .explore-nav-items a:hover {
          opacity: 0.7;
        }

        .explore-nav-items button {
          outline: none;
          border: 2px solid #7dd3fc;
          border-radius: 2em;
          color: #7dd3fc;
          background-color: transparent;
          font-size: 15px;
          padding: 0.5em 1.5em;
          cursor: pointer;
          transition: all 0.2s;
        }

        .explore-nav-items button:hover {
          background-color: #7dd3fc;
          color: #0a1628;
        }

        .explore-footer {
          position: fixed;
          bottom: 0;
          left: 0;
          width: 100vw;
          padding: 2em;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          z-index: 9999;
        }

        .explore-footer h2 {
          font-size: 28px;
          font-weight: 400;
          line-height: 1.35;
          color: #7dd3fc;
          width: 45%;
          text-shadow: 0 2px 20px rgba(0, 0, 0, 0.5);
        }

        .explore-footer-links {
          display: flex;
          gap: 2em;
        }

        .explore-footer-links p {
          font-size: 15px;
          color: #7dd3fc;
          cursor: pointer;
          transition: opacity 0.2s;
        }

        .explore-footer-links p:hover {
          opacity: 0.7;
        }

        @media (max-width: 768px) {
          .explore-footer h2 {
            font-size: 20px;
            width: 60%;
          }
        }
      `}</style>

      <div className="explore-page">
        {/* YouTube Video Background */}
        <div className="explore-video-container">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
            title="Background Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* Water Ripple Canvas (on top of video) */}
        <div className="explore-canvas-container" ref={containerRef} />

        {/* Navigation */}
        <nav className="explore-nav">
          <div className="explore-logo">
            <p>In Deep Water</p>
          </div>

          <div className="explore-nav-items">
            <Link to="/test">Ripples</Link>
            <Link to="/map">Research</Link>
            <Link to="/about">Data</Link>
            <Link to="/"><button>Get Started</button></Link>
          </div>
        </nav>

        {/* Footer */}
        <footer className="explore-footer">
          <h2>Protecting our oceans through real-time monitoring and data-driven conservation</h2>

          <div className="explore-footer-links">
            <p>About</p>
            <p>Contact</p>
          </div>
        </footer>
      </div>
    </>
  );
}
